<template>
    <!--
        Drop-in replacement for the eonasdan/moment.js-backed DateTimePicker.vue,
        built on @vuepic/vue-datepicker (Vue-native, no moment dependency).
        Keeps the same public API: v-model is a JS Date and we emit a JS Date.
        Display formatting still uses moment-style tokens (translated to Luxon).
    -->
    <vue-datepicker
        :action-row="{ showCancel: false }"
        :auto-apply="true"
        :clearable="clearable"
        ref="picker"
        :config="{ closeOnAutoApply: !keepOpenOnSelect, keepActionRow: keepOpenOnSelect }"
        :enable-time-picker="enableTime"
        :enable-minutes="enableMinutes"
        :enable-seconds="enableSeconds"
        :is-24="is24"
        :format="formatFn"
        :max-date="coercedMaxDate"
        :min-date="coercedMinDate"
        :model-value="internalValue"
        :month-picker="isMonthPicker"
        :name="name"
        :placeholder="placeholder"
        :time-picker="isTimePicker"
        :teleport="true"
        :ui="{ input: inputClasses }"
        :uid="id"
        @date-update="onDateUpdate"
        @update:model-value="onUpdate"
    ></vue-datepicker>
</template>

<script>
import VueDatepicker from '@vuepic/vue-datepicker'
import { DateTime } from 'luxon'
import { v4 as uuidv4 } from 'uuid'

export default {
    name: "VueDatePicker",
    // The inner <vue-datepicker> is this component's root element, so any
    // undeclared listener on the wrapper would fall through onto it. Callers
    // bind `v-model:model-value`, whose stray `onUpdate:model-value` listener
    // would then write the picker's raw value (e.g. {month, year} in month
    // mode) straight to the parent, bypassing our Date conversion. Disable
    // fallthrough so only our explicit @update:model-value handler runs.
    inheritAttrs: false,
    components: { VueDatepicker },
    emits: ['update:modelValue'],
    // Mirrors DateTimePicker.vue's public API. Date objects are preferred for value props.
    props: {
        // Date is preferred, but blades seed an initial value as a formatted
        // string (e.g. `model-value="06/15/2026"`); coerce it via currentValue.
        modelValue: [Date, String],
        format: {
            type: String,
            default: 'YYYY-MM-DD HH:mm:ss',
        },
        minDate: {
            type: [Date, String],
            default: null,
        },
        maxDate: {
            type: [Date, String],
            default: null,
        },
        id: {
            type: String,
            default() {
                return 'datetimepicker_' + uuidv4()
            },
        },
        name: {
            type: String,
            default() {
                return 'datetimepicker'
            },
        },
        viewMode: {
            type: String,
            default: null,
        },
        placeholder: {
            type: String,
            default: '',
        },
        // Accepted for API parity with the legacy picker; vue-datepicker lays
        // date + time out together natively, so these are not needed.
        sideBySide: {
            type: Boolean,
            default: false,
        },
        verticalPosition: {
            type: String,
            default: 'auto',
        },
        horizontalPosition: {
            type: String,
            default: 'auto',
        },
        timezone: {
            type: String,
            default: null,
        },
        defaultCurrentTime: {
            type: Boolean,
            default: false,
        },
        // Allow clearing the value to empty (e.g. STAR night start/end → "NONE").
        // Off by default: most consumers call .getHours()/.toDateString() on the
        // bound value and can't tolerate a null. When on, onUpdate emits null.
        clearable: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            inputClasses: 'w-full py-1 px-3 text-gray-800 leading-normal rounded text-base border border-gray-300 text-center',
            // Internal source of truth for the selected value. Seeded from
            // modelValue (which may be a string from a blade), kept in sync with
            // the prop for Vue parents that use v-model, and updated on selection
            // so blade pages — which have no v-model writeback — still retain the
            // picked value (the controlled :model-value would otherwise snap back
            // to the seed on every render). Seeded in created() (not here) so the
            // luxonFormat computed is available for parsing string seeds.
            localValue: null,
        }
    },
    created() {
        this.localValue = this.parseModelValue(this.modelValue)
    },
    watch: {
        modelValue(value) {
            const next = this.parseModelValue(value)
            // Avoid clobbering localValue with an equal value (and breaking the
            // reference) when our own emit round-trips back through v-model.
            if (next?.valueOf() !== this.localValue?.valueOf()) {
                this.localValue = next
            }
        },
    },
    computed: {
        // Month view → month picker; a time-only format (e.g. "HH:mm") → time
        // picker; a format with both date and time tokens → date + time; else
        // a plain date picker.
        hasDateTokens() {
            return /[YMD]/.test(this.format)
        },
        hasTimeTokens() {
            return /[Hhms]/.test(this.format)
        },
        isMonthPicker() {
            return this.viewMode === 'months'
        },
        isTimePicker() {
            return !this.isMonthPicker && this.hasTimeTokens && !this.hasDateTokens
        },
        // Derive the time-of-day granularity from the (moment-style) format so
        // the widget only exposes the units the format actually renders. The
        // hourly alarm schedule uses "h a" (no minute token), where storing a
        // minute would be silently dropped server-side, so its picker must not
        // offer a minutes control. Formats with an "m"/"s" token keep them.
        enableMinutes() {
            return /m/.test(this.format)
        },
        enableSeconds() {
            return /s/.test(this.format)
        },
        // A lowercase "h" or an am/pm token ("a"/"A") means a 12-hour clock
        // with an AM/PM toggle; uppercase "H" is a 24-hour clock.
        is24() {
            return !/[haA]/.test(this.format)
        },
        enableTime() {
            return !this.isMonthPicker && this.hasTimeTokens && this.hasDateTokens
        },
        // Any picker that also picks a time-of-day must NOT close the moment a
        // date (or a single time unit) is chosen — auto-apply's default is to
        // close on the first selection, which forced users to re-open the menu
        // to set the time. We keep auto-apply (so the value still propagates on
        // every change, which blade pages rely on) but hold the menu open and
        // surface the action row's "Select" button as the way to close it.
        // Date+time pickers additionally jump to the time view on selection —
        // see onDateUpdate.
        keepOpenOnSelect() {
            return this.enableTime || this.isTimePicker
        },
        // vue-datepicker expects mode-specific value shapes; convert our JS Date.
        // The bound Date is an absolute instant; callers interpret its wall-clock
        // in `timezone` (see ExcursionReportSettings.parseLogTime/formatLogTime),
        // so we present and read wall-clock-in-`timezone`, not the browser-local
        // wall-clock. The month picker stays local — its callers operate on the
        // Date with local getMonth()/getDate()/setDate().
        internalValue() {
            if (!this.isValidDate(this.localValue)) {
                return null
            }
            if (this.isMonthPicker) {
                return { month: this.localValue.getMonth(), year: this.localValue.getFullYear() }
            }
            if (this.isTimePicker) {
                const dt = DateTime.fromJSDate(this.localValue, { zone: this.timezone || undefined })
                return { hours: dt.hour, minutes: dt.minute, seconds: dt.second }
            }
            return this.toFakeLocal(this.localValue)
        },
        coercedMinDate() {
            return this.toFakeLocal(this.coerceDate(this.minDate))
        },
        coercedMaxDate() {
            return this.toFakeLocal(this.coerceDate(this.maxDate))
        },
        // Translate the moment-style format string to Luxon tokens. Day/year
        // tokens differ; month/hour/minute/second tokens are shared. Kept
        // reactive because callers swap `format` at runtime (month ↔ day view).
        luxonFormat() {
            return this.format
                .replace(/YYYY/g, 'yyyy')
                .replace(/YY/g, 'yy')
                .replace(/DD/g, 'dd')
                .replace(/D/g, 'd')
        },
    },
    methods: {
        isValidDate(date) {
            return date instanceof Date && !isNaN(date.valueOf())
        },
        // Normalize modelValue into the internal JS Date. A string seed from a
        // blade is wall-clock in `timezone` (e.g. a Carbon "2026-06-15 00:00:00"
        // rendered in the company tz), so parse it in that zone to get the right
        // absolute instant — otherwise the browser-local parse in coerceDate,
        // followed by toFakeLocal(), shifts the displayed time by the browser↔
        // company offset.
        parseModelValue(value) {
            if (value instanceof Date || value === null || value === undefined || value === '') {
                return this.coerceDate(value) ?? null
            }
            if (this.timezone) {
                for (const fmt of [this.luxonFormat, 'yyyy-MM-dd HH:mm:ss', 'MM/dd/yyyy HH:mm:ss', 'MM/dd/yyyy HH:mm']) {
                    if (!fmt) {
                        continue
                    }
                    const dt = DateTime.fromFormat(String(value), fmt, { zone: this.timezone })
                    if (dt.isValid) {
                        return dt.toJSDate()
                    }
                }
            }
            return this.coerceDate(value) ?? null
        },
        coerceDate(value) {
            if (value === null || value === undefined || value === '') {
                return undefined
            }
            if (value instanceof Date) {
                return value
            }
            // Native parsing handles full date / datetime strings (and ISO).
            const native = new Date(value)
            if (this.isValidDate(native)) {
                return native
            }
            // Time-only seeds (e.g. STAR's "8:00 PM") don't parse via Date; fall
            // back to Luxon using this picker's format, then a few common shapes.
            // (luxonFormat is a computed and may be unset when seeding from data();
            //  the hardcoded fallbacks still cover the time-only case.)
            for (const fmt of [this.luxonFormat, 'h:mm a', 'h:mm:ss a', 'HH:mm', 'HH:mm:ss']) {
                if (!fmt) {
                    continue
                }
                const parsed = DateTime.fromFormat(String(value), fmt)
                if (parsed.isValid) {
                    return parsed.toJSDate()
                }
            }
            return native
        },
        // vue-datepicker hands the format fn the mode-specific value; normalize
        // back to a JS Date, then render with the (translated) moment tokens.
        formatFn(value) {
            const date = this.toDate(value)
            if (!this.isValidDate(date)) {
                return ''
            }
            return DateTime.fromJSDate(date).toFormat(this.luxonFormat)
        },
        toDate(value) {
            if (value instanceof Date) {
                return value
            }
            if (value && typeof value === 'object' && 'year' in value) {
                return new Date(value.year, value.month, 1)
            }
            if (value && typeof value === 'object' && 'hours' in value) {
                const base = this.isValidDate(this.localValue) ? new Date(this.localValue) : new Date()
                base.setHours(value.hours ?? 0, value.minutes ?? 0, value.seconds ?? 0, 0)
                return base
            }
            return null
        },
        // Advance to the time view as soon as a day is clicked, so a date+time
        // picker doesn't strand the user on the calendar with the time unset.
        //
        // vue-datepicker's own `flow` prop does this too, but only once per
        // open: its step counter walks forward on each selection and latches at
        // -1 when it passes the last step, so after the user navigates back to
        // the calendar a second date click stops advancing until the menu is
        // reopened. Calling switchView directly is stateless and repeatable.
        onDateUpdate() {
            if (!this.enableTime) {
                return
            }
            // Let the picker finish handling the click (it can re-render the
            // calendar) before switching the view out from under it.
            this.$nextTick(() => this.$refs.picker?.switchView('time'))
        },
        onUpdate(value) {
            if (value === null || value === undefined) {
                // Only propagate a clear when the consumer opted into it; otherwise
                // ignore it — consumers call .toDateString()/.getHours() on the value.
                if (this.clearable) {
                    this.localValue = null
                    this.$emit('update:modelValue', null)
                }
                return
            }
            // Branch on the *shape* vue-datepicker hands back, not on the mode
            // flags: the flags can be momentarily out of sync with the picker
            // mid-render, which previously leaked a raw {month, year} object into
            // the parent's v-model (TypeError: ....getDate is not a function).
            let result
            if (value instanceof Date) {
                result = this.fromFakeLocal(value)
            } else if (value && typeof value === 'object' && 'year' in value) {
                result = this.dateFromMonth(value)
            } else if (value && typeof value === 'object' && 'hours' in value) {
                result = this.dateFromTime(value)
            } else {
                result = new Date(value)
            }
            if (this.isValidDate(result)) {
                // Keep our internal state in sync so blade pages (no v-model
                // writeback) retain the selection, then notify Vue parents.
                this.localValue = result
                this.$emit('update:modelValue', result)
            }
        },
        // Date <-> "fake local" helpers for timezone-aware date/datetime modes:
        // vue-datepicker reads/writes a Date's *browser-local* wall-clock, so to
        // show the `timezone` wall-clock we hand it a Date whose local components
        // equal the zoned components, and reverse that on the way back out.
        toFakeLocal(date) {
            if (!this.timezone || !this.isValidDate(date)) {
                return date
            }
            const dt = DateTime.fromJSDate(date, { zone: this.timezone })
            return new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute, dt.second, dt.millisecond)
        },
        fromFakeLocal(date) {
            if (!this.timezone || !this.isValidDate(date)) {
                return date
            }
            return DateTime.fromObject({
                year: date.getFullYear(),
                month: date.getMonth() + 1,
                day: date.getDate(),
                hour: date.getHours(),
                minute: date.getMinutes(),
                second: date.getSeconds(),
                millisecond: date.getMilliseconds(),
            }, { zone: this.timezone }).toJSDate()
        },
        // Rebuild the absolute instant from picked wall-clock time, keeping the
        // date part and interpreting the result in `timezone`.
        dateFromTime(value) {
            const zone = this.timezone || undefined
            const base = this.isValidDate(this.localValue)
                ? DateTime.fromJSDate(this.localValue, { zone })
                : DateTime.now().setZone(zone)
            return base.set({
                hour: value.hours ?? 0,
                minute: value.minutes ?? 0,
                second: value.seconds ?? 0,
                millisecond: 0,
            }).toJSDate()
        },
        // Preserve the previously bound day-of-month and time when only the
        // month/year changes (matches how the legacy month view behaved).
        dateFromMonth(value) {
            const base = this.isValidDate(this.localValue) ? new Date(this.localValue) : new Date()
            const day = base.getDate()
            const result = new Date(base)
            result.setFullYear(value.year, value.month, 1)
            const daysInMonth = new Date(value.year, value.month + 1, 0).getDate()
            result.setDate(Math.min(day, daysInMonth))
            return result
        },
    },
}
</script>

<style scoped>
</style>
